import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import attendanceService from "./attendanceService";

const getUserFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const statusColor = (status) => {
  const map = {
    Present: "green",
    Absent: "red",
    HalfDay: "orange",
    Leave: "purple",
    Holiday: "blue",
    WeekOff: "gray",
  };
  return map[status] || "gray";
};

const AttendancePage = ({ defaultTab = "daily" }) => {
  const toast = useToast();
  const user = getUserFromStorage();

  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);

  const [dailyRecords, setDailyRecords] = useState([]);
  const [dailyFilters, setDailyFilters] = useState({
    fromDate: "",
    toDate: "",
    status: "",
  });

  const [report, setReport] = useState(null);
  const [reportPeriod, setReportPeriod] = useState("month");

  const [shifts, setShifts] = useState([]);
  const [newShift, setNewShift] = useState({
    name: "",
    startTime: "09:00",
    endTime: "18:00",
    graceMinutes: 15,
    minHalfDayMinutes: 240,
    minFullDayMinutes: 480,
    overtimeEligible: true,
    overtimeAfterMinutes: 480,
  });

  const [schedules, setSchedules] = useState([]);
  const [scheduleForm, setScheduleForm] = useState({
    name: "",
    shiftId: "",
  });

  const [rosters, setRosters] = useState([]);
  const [rosterForm, setRosterForm] = useState({
    userId: user?._id || "",
    date: "",
    shiftId: "",
  });

  const [geofences, setGeofences] = useState([]);
  const [geofenceForm, setGeofenceForm] = useState({
    name: "",
    centerLat: "",
    centerLng: "",
    radius: 100,
  });

  const weeklyConfig = useMemo(() => {
    if (!scheduleForm.shiftId) return [];
    return [0, 1, 2, 3, 4, 5, 6].map((day) => ({
      dayOfWeek: day,
      shiftId: scheduleForm.shiftId,
      isWorkingDay: day !== 0,
    }));
  }, [scheduleForm.shiftId]);

  const loadDaily = async () => {
    setLoading(true);
    try {
      const response = await attendanceService.getDailyAttendance({
        userId: user?._id,
        ...dailyFilters,
      });
      setDailyRecords(response || []);
    } catch (error) {
      toast({
        title: "Failed to fetch attendance",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    try {
      const data = await attendanceService.getAttendanceReport({
        period: reportPeriod,
        userId: user?._id,
      });
      setReport(data);
    } catch (error) {
      toast({
        title: "Failed to fetch report",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const loadShiftsAndSchedules = async () => {
    try {
      const [shiftData, scheduleData] = await Promise.all([
        attendanceService.listShifts(),
        attendanceService.listSchedules(),
      ]);
      setShifts(shiftData || []);
      setSchedules(scheduleData || []);
    } catch (error) {
      toast({
        title: "Failed to load shifts/schedules",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const loadGeofences = async () => {
    try {
      const data = await attendanceService.listGeofences();
      setGeofences(data || []);
    } catch (error) {
      toast({
        title: "Failed to load geofences",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const loadRoster = async () => {
    if (!rosterForm.userId) return;
    const now = new Date();
    const fromDate = new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .slice(0, 10);
    const toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      .toISOString()
      .slice(0, 10);

    try {
      const data = await attendanceService.listRosterForUser(
        rosterForm.userId,
        {
          fromDate,
          toDate,
        },
      );
      setRosters(data || []);
    } catch (error) {
      setRosters([]);
      toast({
        title: "Roster load skipped",
        description: error.response?.data?.error || "No roster data found",
        status: "info",
      });
    }
  };

  useEffect(() => {
    setTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    loadDaily();
    loadReport();
    loadShiftsAndSchedules();
    loadGeofences();
  }, []);

  useEffect(() => {
    if (tab === "roster") {
      loadRoster();
    }
  }, [tab]);

  const createShift = async () => {
    try {
      await attendanceService.createShift(newShift);
      toast({ title: "Shift created", status: "success" });
      setNewShift({
        name: "",
        startTime: "09:00",
        endTime: "18:00",
        graceMinutes: 15,
        minHalfDayMinutes: 240,
        minFullDayMinutes: 480,
        overtimeEligible: true,
        overtimeAfterMinutes: 480,
      });
      loadShiftsAndSchedules();
    } catch (error) {
      toast({
        title: "Shift create failed",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const createSchedule = async () => {
    if (!scheduleForm.shiftId || !scheduleForm.name) {
      toast({
        title: "Schedule name and shift are required",
        status: "warning",
      });
      return;
    }
    try {
      await attendanceService.createSchedule({
        name: scheduleForm.name,
        weeklyConfig,
      });
      toast({ title: "Schedule created", status: "success" });
      setScheduleForm({ name: "", shiftId: "" });
      loadShiftsAndSchedules();
    } catch (error) {
      toast({
        title: "Schedule create failed",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const createRoster = async () => {
    if (!rosterForm.userId || !rosterForm.date || !rosterForm.shiftId) {
      toast({ title: "User, date and shift are required", status: "warning" });
      return;
    }
    try {
      await attendanceService.createRoster(rosterForm);
      toast({ title: "Roster entry created", status: "success" });
      setRosterForm((prev) => ({ ...prev, date: "", shiftId: "" }));
      loadRoster();
    } catch (error) {
      toast({
        title: "Roster create failed",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const createGeofence = async () => {
    if (
      !geofenceForm.name ||
      !geofenceForm.centerLat ||
      !geofenceForm.centerLng
    ) {
      toast({
        title: "Name and center coordinates are required",
        status: "warning",
      });
      return;
    }
    try {
      await attendanceService.createGeofence({
        name: geofenceForm.name,
        type: "CIRCLE",
        coordinates: {
          center: {
            lat: Number(geofenceForm.centerLat),
            lng: Number(geofenceForm.centerLng),
          },
        },
        radius: Number(geofenceForm.radius),
      });
      toast({ title: "Geofence created", status: "success" });
      setGeofenceForm({ name: "", centerLat: "", centerLng: "", radius: 100 });
      loadGeofences();
    } catch (error) {
      toast({
        title: "Geofence create failed",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const tabButton = (value, label) => (
    <Button
      key={value}
      size="sm"
      variant={tab === value ? "solid" : "outline"}
      colorScheme="teal"
      onClick={() => setTab(value)}
    >
      {label}
    </Button>
  );

  return (
    <Box p={{ base: 4, md: 6 }}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg">Attendance & Workforce Operations</Heading>
          <Text mt={1} color="gray.500">
            Integrated with attendance, shift, schedule, roster and geofence
            APIs.
          </Text>
        </Box>

        <HStack spacing={2} wrap="wrap">
          {tabButton("daily", "Daily Attendance")}
          {tabButton("report", "Reports")}
          {tabButton("shift", "Shifts & Schedules")}
          {tabButton("roster", "Rosters")}
          {tabButton("geofence", "Geofences")}
        </HStack>

        {loading && (
          <HStack>
            <Spinner size="sm" />
            <Text>Loading...</Text>
          </HStack>
        )}

        {tab === "daily" && (
          <VStack align="stretch" spacing={4}>
            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              }}
              gap={3}
            >
              <FormControl>
                <FormLabel>From</FormLabel>
                <Input
                  type="date"
                  value={dailyFilters.fromDate}
                  onChange={(e) =>
                    setDailyFilters((prev) => ({
                      ...prev,
                      fromDate: e.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>To</FormLabel>
                <Input
                  type="date"
                  value={dailyFilters.toDate}
                  onChange={(e) =>
                    setDailyFilters((prev) => ({
                      ...prev,
                      toDate: e.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={dailyFilters.status}
                  onChange={(e) =>
                    setDailyFilters((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  <option value="Present">Present</option>
                  <option value="Absent">Absent</option>
                  <option value="HalfDay">HalfDay</option>
                  <option value="Leave">Leave</option>
                  <option value="Holiday">Holiday</option>
                  <option value="WeekOff">WeekOff</option>
                </Select>
              </FormControl>
              <FormControl display="flex" alignItems="end">
                <Button colorScheme="teal" onClick={loadDaily} width="full">
                  Refresh
                </Button>
              </FormControl>
            </Grid>

            <Box display={{ base: "none", md: "block" }}>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Status</Th>
                    <Th>First In</Th>
                    <Th>Last Out</Th>
                    <Th isNumeric>Work (min)</Th>
                    <Th isNumeric>Late</Th>
                    <Th isNumeric>Overtime</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {dailyRecords.map((record) => (
                    <Tr key={record._id}>
                      <Td>{new Date(record.date).toLocaleDateString()}</Td>
                      <Td>
                        <Badge colorScheme={statusColor(record.status)}>
                          {record.status}
                        </Badge>
                      </Td>
                      <Td>
                        {record.firstIn
                          ? new Date(record.firstIn).toLocaleTimeString()
                          : "-"}
                      </Td>
                      <Td>
                        {record.lastOut
                          ? new Date(record.lastOut).toLocaleTimeString()
                          : "-"}
                      </Td>
                      <Td isNumeric>{record.totalWorkMinutes || 0}</Td>
                      <Td isNumeric>{record.lateMinutes || 0}</Td>
                      <Td isNumeric>{record.overtimeMinutes || 0}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            <Box display={{ base: "block", md: "none" }}>
              {dailyRecords.length === 0 ? (
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  textAlign="center"
                >
                  <Text color="gray.500">No attendance records found</Text>
                </Box>
              ) : (
                <SimpleGrid columns={1} spacing={3}>
                  {dailyRecords.map((record) => (
                    <Box
                      key={record._id}
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                    >
                      <HStack justify="space-between" mb={2}>
                        <Text fontWeight="600">
                          {new Date(record.date).toLocaleDateString()}
                        </Text>
                        <Badge colorScheme={statusColor(record.status)}>
                          {record.status}
                        </Badge>
                      </HStack>
                      <Divider my={2} />
                      <SimpleGrid columns={2} spacing={2}>
                        <Box>
                          <Text fontSize="xs" color="gray.500">
                            First In
                          </Text>
                          <Text fontSize="sm">
                            {record.firstIn
                              ? new Date(record.firstIn).toLocaleTimeString()
                              : "-"}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500">
                            Last Out
                          </Text>
                          <Text fontSize="sm">
                            {record.lastOut
                              ? new Date(record.lastOut).toLocaleTimeString()
                              : "-"}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500">
                            Work (min)
                          </Text>
                          <Text fontSize="sm">
                            {record.totalWorkMinutes || 0}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500">
                            Overtime
                          </Text>
                          <Text fontSize="sm">
                            {record.overtimeMinutes || 0}
                          </Text>
                        </Box>
                      </SimpleGrid>
                    </Box>
                  ))}
                </SimpleGrid>
              )}
            </Box>
          </VStack>
        )}

        {tab === "report" && (
          <VStack align="stretch" spacing={4}>
            <Stack direction={{ base: "column", md: "row" }} spacing={3}>
              <Select
                maxW={{ base: "full", md: "220px" }}
                value={reportPeriod}
                onChange={(e) => setReportPeriod(e.target.value)}
              >
                <option value="month">Month</option>
                <option value="year">Year</option>
              </Select>
              <Button colorScheme="teal" onClick={loadReport}>
                Load Report
              </Button>
            </Stack>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(3, minmax(0, 1fr))" }}
              gap={4}
            >
              {(report?.statusBreakdown || []).map((item) => (
                <Stat key={item._id} p={4} borderWidth="1px" borderRadius="md">
                  <StatLabel>{item._id}</StatLabel>
                  <StatNumber>{item.count}</StatNumber>
                </Stat>
              ))}
            </Grid>
          </VStack>
        )}

        {tab === "shift" && (
          <VStack align="stretch" spacing={5}>
            <Box borderWidth="1px" borderRadius="md" p={4}>
              <Heading size="sm" mb={3}>
                Create Shift
              </Heading>
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                }}
                gap={3}
              >
                <Input
                  placeholder="Shift name"
                  value={newShift.name}
                  onChange={(e) =>
                    setNewShift((p) => ({ ...p, name: e.target.value }))
                  }
                />
                <Input
                  type="time"
                  value={newShift.startTime}
                  onChange={(e) =>
                    setNewShift((p) => ({ ...p, startTime: e.target.value }))
                  }
                />
                <Input
                  type="time"
                  value={newShift.endTime}
                  onChange={(e) =>
                    setNewShift((p) => ({ ...p, endTime: e.target.value }))
                  }
                />
                <NumberInput
                  value={newShift.graceMinutes}
                  onChange={(_, value) =>
                    setNewShift((p) => ({ ...p, graceMinutes: value || 0 }))
                  }
                >
                  <NumberInputField placeholder="Grace (min)" />
                </NumberInput>
              </Grid>
              <Button mt={3} colorScheme="teal" onClick={createShift}>
                Add Shift
              </Button>
            </Box>

            <Box borderWidth="1px" borderRadius="md" p={4}>
              <Heading size="sm" mb={3}>
                Create Schedule
              </Heading>
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(3, minmax(0, 1fr))",
                }}
                gap={3}
              >
                <Input
                  placeholder="Schedule name"
                  value={scheduleForm.name}
                  onChange={(e) =>
                    setScheduleForm((p) => ({ ...p, name: e.target.value }))
                  }
                />
                <Select
                  placeholder="Select shift"
                  value={scheduleForm.shiftId}
                  onChange={(e) =>
                    setScheduleForm((p) => ({ ...p, shiftId: e.target.value }))
                  }
                >
                  {shifts.map((shift) => (
                    <option key={shift._id} value={shift._id}>
                      {shift.name}
                    </option>
                  ))}
                </Select>
                <Button colorScheme="teal" onClick={createSchedule}>
                  Add Schedule
                </Button>
              </Grid>
            </Box>

            <Grid
              templateColumns={{ base: "1fr", xl: "repeat(2, minmax(0, 1fr))" }}
              gap={4}
            >
              <Box borderWidth="1px" borderRadius="md" p={4}>
                <Heading size="sm" mb={3}>
                  Shifts
                </Heading>
                {shifts.map((shift) => (
                  <HStack key={shift._id} justify="space-between" py={2}>
                    <Text>
                      {shift.name} ({shift.startTime} - {shift.endTime})
                    </Text>
                    <Badge colorScheme={shift.isActive ? "green" : "gray"}>
                      {shift.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </HStack>
                ))}
              </Box>
              <Box borderWidth="1px" borderRadius="md" p={4}>
                <Heading size="sm" mb={3}>
                  Schedules
                </Heading>
                {schedules.map((schedule) => (
                  <HStack key={schedule._id} justify="space-between" py={2}>
                    <Text>{schedule.name}</Text>
                    <Badge colorScheme={schedule.isDefault ? "purple" : "blue"}>
                      {schedule.isDefault ? "Default" : "Custom"}
                    </Badge>
                  </HStack>
                ))}
              </Box>
            </Grid>
          </VStack>
        )}

        {tab === "roster" && (
          <VStack align="stretch" spacing={4}>
            <Box borderWidth="1px" borderRadius="md" p={4}>
              <Heading size="sm" mb={3}>
                Create Roster Entry
              </Heading>
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                }}
                gap={3}
              >
                <Input
                  placeholder="User ID"
                  value={rosterForm.userId}
                  onChange={(e) =>
                    setRosterForm((p) => ({ ...p, userId: e.target.value }))
                  }
                />
                <Input
                  type="date"
                  value={rosterForm.date}
                  onChange={(e) =>
                    setRosterForm((p) => ({ ...p, date: e.target.value }))
                  }
                />
                <Select
                  placeholder="Select shift"
                  value={rosterForm.shiftId}
                  onChange={(e) =>
                    setRosterForm((p) => ({ ...p, shiftId: e.target.value }))
                  }
                >
                  {shifts.map((shift) => (
                    <option key={shift._id} value={shift._id}>
                      {shift.name}
                    </option>
                  ))}
                </Select>
                <Button colorScheme="teal" onClick={createRoster}>
                  Save
                </Button>
              </Grid>
            </Box>

            <Box display={{ base: "none", md: "block" }}>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>User</Th>
                    <Th>Shift</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rosters.map((item) => (
                    <Tr key={item._id}>
                      <Td>{new Date(item.date).toLocaleDateString()}</Td>
                      <Td>{item.userId?.name || item.userId || "-"}</Td>
                      <Td>{item.shiftId?.name || "-"}</Td>
                      <Td>
                        <Badge colorScheme={item.isActive ? "green" : "gray"}>
                          {item.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            <Box display={{ base: "block", md: "none" }}>
              {rosters.length === 0 ? (
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  textAlign="center"
                >
                  <Text color="gray.500">No roster entries found</Text>
                </Box>
              ) : (
                <SimpleGrid columns={1} spacing={3}>
                  {rosters.map((item) => (
                    <Box
                      key={item._id}
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                    >
                      <HStack justify="space-between" mb={2}>
                        <Text fontWeight="600">
                          {new Date(item.date).toLocaleDateString()}
                        </Text>
                        <Badge colorScheme={item.isActive ? "green" : "gray"}>
                          {item.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </HStack>
                      <Text fontSize="sm" color="gray.600">
                        User: {item.userId?.name || item.userId || "-"}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Shift: {item.shiftId?.name || "-"}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              )}
            </Box>
          </VStack>
        )}

        {tab === "geofence" && (
          <VStack align="stretch" spacing={4}>
            <Box borderWidth="1px" borderRadius="md" p={4}>
              <Heading size="sm" mb={3}>
                Create Geofence (Circle)
              </Heading>
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                }}
                gap={3}
              >
                <Input
                  placeholder="Name"
                  value={geofenceForm.name}
                  onChange={(e) =>
                    setGeofenceForm((p) => ({ ...p, name: e.target.value }))
                  }
                />
                <Input
                  placeholder="Center Latitude"
                  value={geofenceForm.centerLat}
                  onChange={(e) =>
                    setGeofenceForm((p) => ({
                      ...p,
                      centerLat: e.target.value,
                    }))
                  }
                />
                <Input
                  placeholder="Center Longitude"
                  value={geofenceForm.centerLng}
                  onChange={(e) =>
                    setGeofenceForm((p) => ({
                      ...p,
                      centerLng: e.target.value,
                    }))
                  }
                />
                <NumberInput
                  min={1}
                  value={geofenceForm.radius}
                  onChange={(_, value) =>
                    setGeofenceForm((p) => ({ ...p, radius: value || 1 }))
                  }
                >
                  <NumberInputField placeholder="Radius (meters)" />
                </NumberInput>
              </Grid>
              <Button mt={3} colorScheme="teal" onClick={createGeofence}>
                Add Geofence
              </Button>
            </Box>

            <Box display={{ base: "none", md: "block" }}>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Type</Th>
                    <Th isNumeric>Radius</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {geofences.map((item) => (
                    <Tr key={item._id}>
                      <Td>{item.name}</Td>
                      <Td>{item.type}</Td>
                      <Td isNumeric>{item.radius || 0}</Td>
                      <Td>
                        <Badge colorScheme={item.isActive ? "green" : "gray"}>
                          {item.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            <Box display={{ base: "block", md: "none" }}>
              {geofences.length === 0 ? (
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  textAlign="center"
                >
                  <Text color="gray.500">No geofences found</Text>
                </Box>
              ) : (
                <SimpleGrid columns={1} spacing={3}>
                  {geofences.map((item) => (
                    <Box
                      key={item._id}
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                    >
                      <HStack justify="space-between" mb={2}>
                        <Text fontWeight="600">{item.name}</Text>
                        <Badge colorScheme={item.isActive ? "green" : "gray"}>
                          {item.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </HStack>
                      <SimpleGrid columns={2} spacing={2}>
                        <Box>
                          <Text fontSize="xs" color="gray.500">
                            Type
                          </Text>
                          <Text fontSize="sm">{item.type}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500">
                            Radius
                          </Text>
                          <Text fontSize="sm">{item.radius || 0}</Text>
                        </Box>
                      </SimpleGrid>
                    </Box>
                  ))}
                </SimpleGrid>
              )}
            </Box>
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

export default AttendancePage;
